import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

const CompactFilterBar = ({
  filters = [],
  onFilterChange,
  onClearFilters,
  showClearButton = true,
  className = '',
  compactMode = false,
  mobileModal = false,
  iconOnly = false, // New prop for icon-only mode
  iconClassName = '',
  expandedByDefault = false,
  inlineSearch = false // New prop for inline search mode
}) => {
  const { t } = useLanguage();
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState({});
  const [isExpanded, setIsExpanded] = useState(expandedByDefault);

  // Initialize local filters from props
  useEffect(() => {
    const initialFilters = {};
    filters.forEach(filter => {
      initialFilters[filter.key] = filter.value || '';
    });
    setLocalFilters(initialFilters);
  }, [filters]);

  // Keyboard shortcut to close expanded filter
  useEffect(() => {
    if (iconOnly && isExpanded) {
      const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
          setIsExpanded(false);
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [iconOnly, isExpanded]);


  const handleFilterChange = (key, value) => {
    // Update local state immediately for responsive UI
    const updatedFilters = { ...localFilters, [key]: value };
    setLocalFilters(updatedFilters);

    // Call parent onFilterChange immediately for continuous typing
    if (onFilterChange) {
      onFilterChange(updatedFilters);
    }
  };

  const handleClearAll = () => {
    const clearedFilters = {};
    filters.forEach(filter => {
      clearedFilters[filter.key] = '';
    });
    setLocalFilters(clearedFilters);
    onFilterChange?.(clearedFilters);
    onClearFilters?.();
  };

  const hasActiveFilters = Object.values(localFilters).some(value =>
    value !== '' && value !== null && value !== undefined
  );

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const FilterInput = ({ filter, className = '' }) => {
    const baseClasses = `w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${className}`;

    switch (filter.type) {
      case 'select':
        return (
          <AppSelect
            value={localFilters[filter.key] || ''}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            placeholder={filter.placeholder || `All ${filter.label}`}
            aria-label={filter.label}
            sheetTitle={filter.label}
            options={[
              { value: '', label: filter.placeholder || `All ${filter.label}` },
              ...(filter.options?.map((option, index) => ({
                value: option.value,
                label: option.label,
                key: index,
              })) || []),
            ]}
            triggerClassName={`${APP_SELECT_FILTER_TRIGGER} ${className}`}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={localFilters[filter.key] || ''}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            className={baseClasses}
            placeholder={filter.placeholder}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={localFilters[filter.key] || ''}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            className={baseClasses}
            placeholder={filter.placeholder}
            min={filter.min}
            max={filter.max}
            step={filter.step}
          />
        );

      case 'text':
      default:
        return (
          <div className="relative">
            <input
              type="text"
              value={localFilters[filter.key] || ''}
              onChange={(e) => handleFilterChange(filter.key, e.target.value)}
              className={`${baseClasses} ${localFilters[filter.key] ? 'pr-8' : ''}`}
              placeholder={filter.placeholder || `Search ${filter.label}`}
              autoComplete="off"
            />
            {localFilters[filter.key] && (
              <button
                onClick={() => handleFilterChange(filter.key, '')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                title="Clear"
              >
                ✕
              </button>
            )}
          </div>
        );
    }
  };

  // Mobile Filter Modal
  if (mobileModal && showMobileFilters) {
    return (
      <>
        {/* Mobile Filter Trigger */}
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => setShowMobileFilters(true)}
            className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span className="text-sm font-medium">Filters</span>
            {hasActiveFilters && (
              <span className="bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {Object.values(localFilters).filter(v => v !== '').length}
              </span>
            )}
          </button>
        </div>

        {/* Mobile Filter Modal */}
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Filters</h3>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {filters.map((filter, index) => (
                  <div key={filter.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {filter.label}
                    </label>
                    <FilterInput filter={filter} />
                  </div>
                ))}
              </div>

              <div className="flex gap-3 mt-6">
                {showClearButton && hasActiveFilters && (
                  <button
                    onClick={handleClearAll}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Clear All
                  </button>
                )}
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Icon-only mode rendering
  if (iconOnly) {
    return (
      <div className="relative">
        {/* Filter Icon Button */}
        <button
          onClick={toggleExpanded}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 hover:shadow-md ${
            hasActiveFilters
              ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
              : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
          } ${iconClassName}`}
          title={isExpanded ? 'Hide Filters' : 'Show Filters'}
        >
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <span className="text-sm font-medium">
            Filters
            {hasActiveFilters && (
              <span className="ml-1 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {Object.values(localFilters).filter(v => v !== '').length}
              </span>
            )}
          </span>
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Expanded Filter Panel */}
        {isExpanded && (
          <div className={`absolute top-full left-0 right-0 mt-2 z-10 bg-white border border-gray-200 rounded-lg shadow-lg p-4 ${className}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <h4 className="text-sm font-medium text-gray-700">Filters</h4>
                {hasActiveFilters && (
                  <span className="bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {Object.values(localFilters).filter(v => v !== '').length}
                  </span>
                )}
              </div>

              {showClearButton && hasActiveFilters && (
                <button
                  onClick={() => {
                    handleClearAll();
                    setIsExpanded(false);
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                >
                  Clear All
                </button>
              )}
            </div>

            <div className={`grid gap-3 ${compactMode ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}>
              {filters.map((filter, index) => (
                <div key={filter.key} className={filter.fullWidth ? 'sm:col-span-2 lg:col-span-3 xl:col-span-4' : ''}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {filter.label}
                  </label>
                  <FilterInput filter={filter} />
                </div>
              ))}
            </div>

            {/* Quick Filter Presets */}
            {filters.some(f => f.presets) && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-gray-500 mr-2">Quick:</span>
                  {filters
                    .filter(f => f.presets)
                    .map(f => f.presets.map((preset, idx) => (
                      <button
                        key={`${f.key}-${idx}`}
                        onClick={() => handleFilterChange(f.key, preset.value)}
                        className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                      >
                        {preset.label}
                      </button>
                    )))
                    .flat()}
                </div>
              </div>
            )}

            {/* Close Button */}
            <div className="flex justify-end mt-3 pt-3 border-t border-gray-200">
              <button
                onClick={() => setIsExpanded(false)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Inline Search Mode - Compact search with expandable filters
  if (inlineSearch) {
    const searchFilter = filters.find(f => f.key === 'search');
    const otherFilters = filters.filter(f => f.key !== 'search');

    return (
      <div className="relative">
        <div className="flex items-center gap-2">
          {/* Inline Search Field */}
          {searchFilter && (
            <div className="relative flex-1 max-w-md">
              <div className="relative">
                <input
                  type="text"
                  value={localFilters[searchFilter.key] || ''}
                  onChange={(e) => handleFilterChange(searchFilter.key, e.target.value)}
                  className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-colors"
                  placeholder={searchFilter.placeholder || 'Search...'}
                  autoComplete="off"
                />
                {/* Search Icon */}
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {/* Clear Button */}
                {localFilters[searchFilter.key] && (
                  <button
                    onClick={() => handleFilterChange(searchFilter.key, '')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Filter Toggle Button */}
          <div className="flex items-center gap-1">
            {otherFilters.length > 0 && (
              <button
                onClick={toggleExpanded}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-200 hover:shadow-md ${
                  hasActiveFilters && Object.keys(localFilters).some(key => key !== 'search' && localFilters[key] && localFilters[key] !== 'All')
                    ? 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                } ${iconClassName}`}
                title={isExpanded ? 'Hide Filters' : 'Show More Filters'}
              >
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="text-sm font-medium hidden sm:inline">
                  More Filters
                  {hasActiveFilters && Object.keys(localFilters).some(key => key !== 'search' && localFilters[key] && localFilters[key] !== 'All') && (
                    <span className="ml-1 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {Object.keys(localFilters).filter(key => key !== 'search' && localFilters[key] && localFilters[key] !== 'All').length}
                    </span>
                  )}
                </span>
              </button>
            )}

            {/* Clear All Button */}
            {showClearButton && hasActiveFilters && (
              <button
                onClick={handleClearAll}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                title="Clear All Filters"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Expanded Additional Filters */}
        {isExpanded && otherFilters.length > 0 && (
          <div className={`absolute top-full left-0 right-0 mt-2 z-10 bg-white border border-gray-200 rounded-lg shadow-lg p-4 ${className}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <h4 className="text-sm font-medium text-gray-700">Additional Filters</h4>
              </div>

              <button
                onClick={() => setIsExpanded(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className={`grid gap-3 ${compactMode ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}>
              {otherFilters.map((filter, index) => (
                <div key={filter.key} className={filter.fullWidth ? 'sm:col-span-2 lg:col-span-3 xl:col-span-4' : ''}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {filter.label}
                  </label>
                  <FilterInput filter={filter} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop/Tablet Inline Filter Bar
  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 mb-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          <h4 className="text-sm font-medium text-gray-700">Filters</h4>
          {hasActiveFilters && (
            <span className="bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">
              {Object.values(localFilters).filter(v => v !== '').length}
            </span>
          )}
        </div>

        {showClearButton && hasActiveFilters && (
          <button
            onClick={handleClearAll}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Clear All
          </button>
        )}
      </div>

      <div className={`grid gap-3 ${compactMode ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}>
        {filters.map((filter, index) => (
          <div key={filter.key} className={filter.fullWidth ? 'sm:col-span-2 lg:col-span-3 xl:col-span-4' : ''}>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {filter.label}
            </label>
            <FilterInput filter={filter} />
          </div>
        ))}
      </div>

      {/* Quick Filter Presets */}
      {filters.some(f => f.presets) && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 mr-2">Quick:</span>
            {filters
              .filter(f => f.presets)
              .map(f => f.presets.map((preset, idx) => (
                <button
                  key={`${f.key}-${idx}`}
                  onClick={() => handleFilterChange(f.key, preset.value)}
                  className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                >
                  {preset.label}
                </button>
              )))
              .flat()}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompactFilterBar;
